import React, { FC, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Checkbox,
  Dropdown,
  TextArea,
  Card,
  Overlay,
  Button,
  Icons,
} from '@ems/client-design-system';
import {
  getComplaintsFormikProps,
  fieldFormikProps,
  convertDatesToLocale,
  convertDateFromISOToLocale,
  getDisturbanceTypesPlaceholder,
  isIncidentAfterContact,
  normalizeIncidentTime,
} from 'src/@complaints/functions';
import { DateTimePicker } from 'src/components';
// selectors
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
// interfaces
import { ICreateComplaint } from 'src/@complaints/interfaces';
// constants
import { INQUIRY_MAX_LENGTH } from 'src/constants';

export const ComplaintForm: FC<ICreateComplaint> = ({
  formikProps,
  columns,
  recordAlreadyExist,
  twelveHourFormat,
  privacyEnabled,
  reasonItems,
  disturbanceTypes = [],
  reason,
  setReason,
  isAnonymous,
  activePrivacyAgreement,
  incidentAfterContactError,
  resetToContactTime,
}) => {
  const { complaintProps, responseRequiredProps, privacyEnabledProps } = getComplaintsFormikProps(
    columns
  );
  const configSelectors = useConfigSelectors();
  const {
    globals: { timeZone },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      headings: { termsAndConditions: overlayHeading },
    },
  } = languageSelectors.getLanguage();

  const { localeTime } = convertDatesToLocale(null, null, timeZone);

  // date format to locale
  const maxContactDate = convertDateFromISOToLocale(localeTime);

  // state
  const [overlay, setoverlay] = useState<boolean>(false);

  const handleTermsAndConditionClick = event => {
    setoverlay(true);
    event.preventDefault();
  };

  const privacyAgreementLabel = () => {
    if (recordAlreadyExist) {
      return columns.privacyEnabled;
    }
    return (
      <span>
        <span>{columns.agreedTo}</span>
        <a className="complaints-terms-and-conditions" onClick={handleTermsAndConditionClick}>
          {columns.termsAndConditions}
        </a>
      </span>
    );
  };

  const handleDateChange = (newDate: Date, fieldName: string) => {
    const isoTime = DateTime.fromISO(new Date(newDate).toISOString())
      .setZone(timeZone, { keepLocalTime: true })
      .set({ seconds: 0 })
      .toISO();

    if (
      fieldName === 'contactTime' &&
      new Date(isoTime).getTime() < new Date(formikProps.values.incidentTime).getTime()
    ) {
      formikProps.setFieldValue('incidentTime', isoTime);
    }

    formikProps.setFieldValue(fieldName, isoTime);
  };

  // Records loaded with Incident later than Contact (e.g. created via an
  // integration/import that has no equivalent constraint) leave the Incident
  // picker pinned to its Contact-Time cap and effectively uneditable (ALN-1638).
  // Surface this rather than silently rewriting the stored value, so a user
  // editing the record can see it and choose to fix it via resetIncidentTime.
  const incidentAfterContact = isIncidentAfterContact(
    formikProps.values.incidentTime,
    formikProps.values.contactTime
  );

  // Base the cap-lifting decision on how the record started, not its current
  // (possibly just-edited) value - otherwise the moment a user types a new
  // out-of-range value, this recomputes to true and lifts the cap for that
  // same render, letting the new bad value through uncapped. Records that
  // started valid must keep the cap enforced for the whole edit session, so
  // Blueprint still blocks a freshly-created bad value the same as before;
  // records that started bad stay uncapped throughout so they're correctable.
  const incidentStartedAfterContact = isIncidentAfterContact(
    formikProps.initialValues.incidentTime,
    formikProps.initialValues.contactTime
  );
  const maxIncidentDate = incidentStartedAfterContact
    ? undefined
    : convertDateFromISOToLocale(formikProps.values.contactTime);

  const resetIncidentTime = () => {
    formikProps.setFieldValue(
      'incidentTime',
      normalizeIncidentTime(formikProps.values.incidentTime, formikProps.values.contactTime)
    );
  };

  return (
    <Card className="inquiry-card">
      <Overlay
        openState={overlay}
        onClose={() => setoverlay(false)}
        classes={['overlay--complaints-terms-conditions']}>
        <div className="overlay_header">
          <h3
            style={{
              fontWeight: 'normal',
            }}>
            {overlayHeading}
          </h3>
          <Button
            size="s"
            style="subtle"
            iconOnly={true}
            onClick={() => setoverlay(false)}
            aria-label="Close modal"
            className="overlay_close"
            leftIcon={
              <Icons
                iconName="ic-ui-cancel"
                size={16}
                style={{ cursor: 'pointer', fill: '#5a6872' }}
              />
            }
          />
        </div>
        <div
          className="overlay_content"
          dangerouslySetInnerHTML={{
            __html: activePrivacyAgreement ? activePrivacyAgreement.agreement : '',
          }}
        />
      </Overlay>
      <div className="row dropdown">
        <div className="complaints-datetime">
          <span className="complaints-datetime__label">{columns.contactTime}</span>
          <DateTimePicker
            className="col-md-4"
            selectedDate={convertDateFromISOToLocale(formikProps.values.contactTime)}
            onChange={handleDateChange}
            fieldName={'contactTime'}
            maxDate={maxContactDate}
            twelveHourFormat={twelveHourFormat}
          />
        </div>
        <div className="complaints-datetime">
          <span className="complaints-datetime__label">{columns.incidentTime}</span>
          <DateTimePicker
            className="col-md-4"
            selectedDate={convertDateFromISOToLocale(formikProps.values.incidentTime)}
            onChange={handleDateChange}
            fieldName={'incidentTime'}
            maxDate={maxIncidentDate}
            twelveHourFormat={twelveHourFormat}
          />
          {incidentAfterContact && (
            <>
              <p className="complaints-datetime__warning">{incidentAfterContactError}</p>
              <a className="complaints-datetime__reset-link" onClick={resetIncidentTime}>
                {resetToContactTime}
              </a>
            </>
          )}
        </div>
      </div>
      <div className="dropdown dropdown--with-margin">
        <Dropdown
          label={columns.reason}
          placeholderValue={getDisturbanceTypesPlaceholder(disturbanceTypes)}
          searchItems={reasonItems}
          isNullable={false}
          selectedItem={reason}
          updateSelection={item => setReason(item)}
        />
      </div>
      <TextArea
        {...{
          className: 'col-md-9',
          ...fieldFormikProps('complaint', formikProps),
          ...complaintProps,
          maxLength: INQUIRY_MAX_LENGTH,
        }}
      />
      <Checkbox
        {...{
          checked: formikProps.values.responseRequired,
          ...fieldFormikProps('responseRequired', formikProps, isAnonymous),
          ...responseRequiredProps,
        }}
      />
      {privacyEnabled && (
        <Checkbox
          {...{
            checked: formikProps.values.privacyAgreementId
              ? true
              : formikProps.values.privacyEnabled,
            ...fieldFormikProps('privacyEnabled', formikProps),
            ...privacyEnabledProps,
            label: privacyAgreementLabel(),
            disabled: recordAlreadyExist ? true : false,
          }}
        />
      )}
    </Card>
  );
};
